import React, { useEffect, useMemo, useState } from "react";
import { Badge, ErrorBox, Field } from "./common";
import { errorMessage, fmtDate, fmtNum } from "../dmApi";

const LIST_LIMIT = 99;
const EMPTY_FILTERS = { campaign_id: "", platform: "", date_from: "", date_to: "" };

/** Step 7: CRM leads that came from this project's campaigns / ads, by platform. */
function CampaignLeads({ api, projectId, campaigns, refreshKey }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const platforms = useMemo(
    () => [...new Set((campaigns || []).map((c) => c.Platform).filter(Boolean))].sort(),
    [campaigns]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api
      .projectLeads(projectId, params)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(errorMessage(err, "Could not load leads")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, projectId, filters, refreshKey]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const leads = data?.leads || [];
  const byCampaign = (data?.by_campaign || []).filter((c) => !filters.platform || c.Platform === filters.platform);
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">Leads from campaigns</h3>
      <p className="dmp-muted dmp-campaign-leads__hint">
        CRM leads tagged with one of this project's campaigns or ads (set on the client lead form).
      </p>

      <div className="dmp-filters">
        <Field label="Campaign">
          <select value={filters.campaign_id} onChange={set("campaign_id")}>
            <option value="">All campaigns</option>
            {(campaigns || []).map((c) => (
              <option key={c.Campaign_Id} value={c.Campaign_Id}>
                {c.Campaign_Name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Platform">
          <select value={filters.platform} onChange={set("platform")}>
            <option value="">All platforms</option>
            {platforms.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field label="From">
          <input type="date" value={filters.date_from} onChange={set("date_from")} />
        </Field>
        <Field label="To">
          <input type="date" value={filters.date_to} onChange={set("date_to")} />
        </Field>
        {hasFilters ? (
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear
          </button>
        ) : null}
      </div>

      <ErrorBox message={error} />

      {byCampaign.length ? (
        <div className="dmp-chips dmp-campaign-leads__summary">
          {byCampaign.map((c) => (
            <span key={c.Campaign_Id} className="dmp-campaign-leads__chip">
              <strong>{fmtNum(c.Leads)}</strong> {c.Campaign_Name}
              <span className="dmp-muted"> · {c.Platform}</span>
            </span>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="dmp-empty">Loading leads…</div>
      ) : !campaigns?.length ? (
        <div className="dmp-empty">No campaigns yet. Leads appear here once campaigns exist and leads are tagged with them.</div>
      ) : leads.length === 0 ? (
        <div className="dmp-empty">No leads {hasFilters ? "match these filters" : "tagged with this project's campaigns yet"}.</div>
      ) : (
        <div className="dmp-table-wrap">
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Platform</th>
                <th>Campaign</th>
                <th>Ad</th>
                <th>Project / Service</th>
                <th>Status</th>
                <th>Site visit</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.Real_Estate_Lead_Id}>
                  <td>{fmtDate(l.Created_On)}</td>
                  <td>{l.Name || "—"}</td>
                  <td>{l.Phone ? <a href={`tel:${String(l.Phone).replace(/[^\d+]/g, "")}`}>{l.Phone}</a> : "—"}</td>
                  <td>{l.Email ? <a href={`mailto:${l.Email}`}>{l.Email}</a> : "—"}</td>
                  <td>{l.Platform || "—"}</td>
                  <td>{l.Campaign_Name || "—"}</td>
                  <td>{l.Ad_Name || "—"}</td>
                  <td>{l.Project || "—"}</td>
                  <td>
                    <Badge value={l.Status} />
                  </td>
                  <td>{l.Site_Visit ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="dmp-muted dmp-campaign-leads__count">
            {leads.length} lead{leads.length === 1 ? "" : "s"}
            {leads.length >= LIST_LIMIT ? ` (showing the latest ${LIST_LIMIT} — narrow the dates to see older ones)` : ""}
          </p>
        </div>
      )}
    </div>
  );
}

export default CampaignLeads;
